import { forwardRef, useCallback, useEffect, useRef, useState } from 'react';
import type { MutableRefObject, PropsWithoutRef, ReactNode, Ref } from 'react';
import styled, { css } from 'styled-components';

import type { BaseProps, ForwardRefForwardPropsComponent, TestIdProp } from '../../types';
import { defaultThemeProp } from '../../theme';
import Popover from '../Popover';
import type { Delay, PopoverProps } from '../Popover';
import { useConsolidatedRef, useTestIds, useTheme, useUID } from '../../hooks';
import { calculateFontSize, readableHue } from '../../styles';
import { getActiveElement, mouseState, testElForOverflow, withTestIds } from '../../utils';
import { ThemeOverride } from '../Configuration';

import { getTooltipTestIds } from './Tooltip.test-ids';

export interface TooltipProps extends BaseProps, TestIdProp {
  /** Text content for the tooltip. */
  children: ReactNode;
  /** Element to serve as the tooltip's target. */
  target: Element | null;
  /** Elements to ignore from within the tooltip's target. */
  ignoredElements?: TooltipProps['target'][];
  /**
   * Delay showing the tooltip on mouseenter.
   * @default 'short'
   */
  showDelay?: Delay;
  /**
   * Delay hiding the tooltip on mouseleave.
   * @default 'long'
   */
  hideDelay?: Delay;
  /**
   * If true, the Tooltip will render outside of the current DOM hierarchy in a [portal](https://reactjs.org/docs/portals.html).
   * @default true
   */
  portal?: PopoverProps['portal'];
  /**
   * If true, the Tooltip will automatically apply an aria-describedby attribute to its target.
   * @default true
   */
  describeTarget?: boolean;
  /**
   * Sets the placement of the tooltip relative to the target.
   * @default 'bottom'
   */
  placement?: PopoverProps['placement'];
  /**
   * Conditionally renders a tooltip only if the associated element's (or its descendants) text content is truncated.
   * To work, the associated element must be be displayed as a CSS "block box" i.e. block, inline-block, etc...
   * @default false
   */
  smart?: boolean;
  /**
   * The group that the Popover belongs to. Popovers in the same group will be closed when others open.
   * @default 'tooltip'
   */
  groupId?: PopoverProps['groupId'];
  ref?: Ref<HTMLDivElement>;
}

export const StyledTooltip = styled.div(({ theme }) => {
  const fontSize = calculateFontSize(theme.base['font-size'], theme.base['font-scale']);

  return css`
    background-color: ${theme.components.tooltip['background-color']};
    color: ${theme.components.tooltip['foreground-color']};
    font-size: ${fontSize.xxs};
    max-width: 40ch;
    padding: calc(${theme.base.spacing} / 2);
    white-space: pre-line;
    word-break: break-word;
    --scale-in-size: 0;
    --animation-duration: ${theme.base.animation.speed};
  `;
});

StyledTooltip.defaultProps = defaultThemeProp;

const Tooltip: ForwardRefForwardPropsComponent<TooltipProps> = forwardRef(function Tooltip(
  {
    testId,
    children,
    target,
    ignoredElements = [],
    showDelay = 'short',
    hideDelay = 'long',
    placement = 'top',
    describeTarget = true,
    smart,
    groupId = 'tooltip',
    ...restProps
  }: PropsWithoutRef<TooltipProps>,
  ref: TooltipProps['ref']
) {
  const id = useUID();
  const [show, setShow] = useState(false);
  const [ignoreDelay, setIgnoreDelay] = useState(false);
  const tooltipRef = useConsolidatedRef(ref);
  const lastClickedRef: MutableRefObject<EventTarget | null> = useRef(null);
  const testIds = useTestIds(testId, getTooltipTestIds);

  const stringContentOnly =
    typeof children === 'string' ||
    (Array.isArray(children) && children.every(child => typeof child === 'string'));

  const onMouseDown = useCallback(
    ({ target: eTarget }: MouseEvent) => {
      lastClickedRef.current = eTarget;
      if (eTarget === tooltipRef.current || eTarget === target) return;
      setShow(false);
    },
    [target]
  );

  useEffect(() => {
    if (!target) return;

    const showTooltip = () => {
      // This approach may result in a tooltip NOT appearing 1px before it should due to browser specifics. Enhance as needed.
      // https://stackoverflow.com/a/64747288

      const checkTruncation = (element: Element): boolean => {
        if (
          testElForOverflow(element).some(Boolean) &&
          !['absolute', 'fixed'].includes(window.getComputedStyle(element).position)
        )
          return true;

        for (const child of element.children) {
          if (checkTruncation(child)) return true;
        }

        return false;
      };

      setShow(smart ? checkTruncation(target) : true);
    };

    const onFocusIn = () => {
      showTooltip();
    };

    const onFocusOut = () => {
      if (!lastClickedRef.current || lastClickedRef.current !== tooltipRef.current) {
        setShow(false);
      }
      lastClickedRef.current = null;
    };

    const onMouseEnter = () => {
      if (ignoredElements.some(exclusion => exclusion?.matches(':hover')) || mouseState.primary) {
        return;
      }

      showTooltip();
    };

    const onMouseLeave = () => {
      if (getActiveElement() !== target) {
        setShow(false);
      }
    };

    const onKeyDown = ({ key }: KeyboardEvent) => {
      if (key === 'Escape' && show) {
        setIgnoreDelay(true);
        setShow(false);
      }
    };

    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('mousedown', onMouseDown);

    target.addEventListener('focusin', onFocusIn);
    target.addEventListener('focusout', onFocusOut);
    target.addEventListener('mouseenter', onMouseEnter);
    target.addEventListener('mouseleave', onMouseLeave);

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('mousedown', onMouseDown);

      target.removeEventListener('focusin', onFocusIn);
      target.removeEventListener('focusout', onFocusOut);
      target.removeEventListener('mouseenter', onMouseEnter);
      target.removeEventListener('mouseleave', onMouseLeave);
    };
  }, [target, onMouseDown, smart, show]);

  useEffect(() => {
    if (target && describeTarget && show) {
      const describedBy = target.getAttribute('aria-describedby');
      // Set the attribute to the old attribute plus the id
      target.setAttribute('aria-describedby', describedBy ? `${describedBy} ${id}` : id);
    } else if (target) {
      // Get the old attribute
      const describedBy = target.getAttribute('aria-describedby');
      if (describedBy) {
        // If the old attribute matches the id just remove it entirely
        if (describedBy === id) {
          target.removeAttribute('aria-describedby');
        } else {
          // Otherwise we want to just remove the id that was added and reset the attribute to what it was before
          const oldDescribedBy = describedBy.replace(new RegExp(`(?:^|\\s+)${id}`), '');
          target.setAttribute('aria-describedby', oldDescribedBy);
        }
      }
    }
  }, [describeTarget, target, show]);

  useEffect(() => {
    if (!show && ignoreDelay) {
      setIgnoreDelay(false);
    }
  }, [show, ignoreDelay]);

  const theme = useTheme();
  const tooltipTheme = {
    base: {
      palette: {
        'primary-background': theme.components.tooltip['background-color'],
        'foreground-color': theme.components.tooltip['foreground-color'],
        interactive: readableHue(
          theme.base.palette.interactive,
          theme.components.tooltip['background-color']
        )
      }
    },
    components: {
      label: {
        'foreground-color': theme.components.tooltip['foreground-color']
      }
    }
  };

  return (
    <ThemeOverride theme={tooltipTheme}>
      <Popover
        data-testid={testIds.root}
        id={id}
        groupId={groupId}
        {...restProps}
        show={show}
        showDelay={ignoreDelay ? 'none' : showDelay}
        hideDelay={ignoreDelay ? 'none' : hideDelay}
        onMouseOver={() => {
          setShow(true);
        }}
        onMouseOut={() => {
          setShow(false);
        }}
        strategy='fixed'
        as={StyledTooltip}
        role={stringContentOnly ? 'tooltip' : 'dialog'}
        target={target}
        arrow
        placement={placement}
        onMouseDown={onMouseDown}
        hideOnTargetHidden
        ref={tooltipRef}
      >
        {children}
      </Popover>
    </ThemeOverride>
  );
});

export default withTestIds(Tooltip, getTooltipTestIds);
